import type { ReactNode } from "react"

import { cn } from "@/shared/utils/cn"
import { trackClick } from "@/shared/utils/track"

type Variant = "primary" | "gradient" | "accent" | "ghost"
type Size = "md" | "lg"
/**
 * 主题色调：
 *   brand   —— 落地页促销红（默认，invite 等促销页用）
 *   primary —— 官网与 App 侧主题蓝 #01B0FE（promo / download 页用）
 * accent（限时橙）是独立语义色，两种色调下保持一致。
 */
type Tone = "brand" | "primary"

export interface CtaButtonProps {
  children: ReactNode
  onClick?: () => void
  /** 埋点 ID，点击时自动上报 click 事件 */
  trackId?: string
  block?: boolean
  variant?: Variant
  /** 主题色调，默认 brand（促销红） */
  tone?: Tone
  size?: Size
  disabled?: boolean
  loading?: boolean
  /** 呼吸动画，用于主转化按钮吸引注意 */
  pulse?: boolean
  type?: "button" | "submit"
  className?: string
}

const VARIANT: Record<Tone, Record<Variant, string>> = {
  brand: {
    primary: "bg-brand text-white shadow-cta",
    gradient: "bg-brand-gradient text-white shadow-cta",
    accent: "bg-accent-gradient text-white shadow-cta",
    ghost: "border border-brand bg-white text-brand",
  },
  primary: {
    primary: "bg-primary text-white shadow-cta-primary",
    gradient: "bg-primary-gradient text-white shadow-cta-primary",
    accent: "bg-accent-gradient text-white shadow-cta",
    ghost: "border border-primary bg-white text-primary",
  },
}

const SIZE: Record<Size, string> = {
  md: "h-11 text-base",
  lg: "h-12 text-lg",
}

/** 营销主按钮：统一圆角、阴影、点击埋点与按压反馈 */
export function CtaButton({
  children,
  onClick,
  trackId,
  block,
  variant = "gradient",
  tone = "brand",
  size = "md",
  disabled,
  loading,
  pulse,
  type = "button",
  className,
}: CtaButtonProps) {
  const handleClick = () => {
    if (disabled || loading) return
    if (trackId) trackClick(trackId)
    onClick?.()
  }

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center rounded-cta font-semibold transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100",
        VARIANT[tone][variant],
        SIZE[size],
        block && "w-full",
        pulse && "animate-cta-pulse",
        className,
      )}
    >
      {loading ? "…" : children}
    </button>
  )
}
